/**
 * AddToWalletButtons — universal "Add to Apple/Google Wallet" buttons.
 * Drop into any module that issues tickets or vouchers.
 *
 * <AddToWalletButtons kind="ticket" itemId={t.id} />
 * <AddToWalletButtons kind="voucher" itemId={v.id} />
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API, withCredentials: true });

let _availabilityCache = null;

async function getAvailability() {
  if (_availabilityCache) return _availabilityCache;
  try {
    const { data } = await api.get("/api/wallet-pass/availability");
    _availabilityCache = data;
  } catch {
    _availabilityCache = { apple: { enabled: false }, google: { enabled: false } };
  }
  return _availabilityCache;
}

export const AddToWalletButtons = ({ kind, itemId, className = "" }) => {
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    getAvailability().then(setAvail);
  }, []);

  if (!avail || (!avail.apple?.enabled && !avail.google?.enabled)) return null;

  const addApple = () => {
    setBusy("apple");
    window.location.href = `${API}/api/wallet-pass/${kind}/${itemId}/apple`;
    setTimeout(() => setBusy(""), 2500);
  };

  const addGoogle = async () => {
    setBusy("google");
    try {
      const { data } = await api.get(`/api/wallet-pass/${kind}/${itemId}/google`);
      if (data?.save_url) window.open(data.save_url, "_blank", "noopener");
    } catch (e) {
      console.warn("google wallet failed", e);
    }
    setBusy("");
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}
         data-testid={`wallet-buttons-${kind}-${itemId}`}>
      {avail.apple?.enabled && (
        <button
          type="button"
          onClick={addApple}
          disabled={busy === "apple"}
          data-testid={`wallet-apple-${itemId}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-black
                     px-3 py-1.5 text-[11px] font-semibold text-white
                     transition-opacity hover:opacity-80 disabled:opacity-50">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden>
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8.98-.2 1.92-.8 3.15-.74 1.32.11 2.31.63 2.96 1.58-2.73 1.63-2.28 5.53.5 6.68-.51 1.63-1.19 3.24-1.69 4.65ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z"/>
          </svg>
          Add to Apple Wallet
        </button>
      )}
      {avail.google?.enabled && (
        <button
          type="button"
          onClick={addGoogle}
          disabled={busy === "google"}
          data-testid={`wallet-google-${itemId}`}
          className="inline-flex items-center gap-1.5 rounded-lg border
                     border-stone-800 bg-stone-900 px-3 py-1.5 text-[11px]
                     font-semibold text-white transition-opacity
                     hover:opacity-80 disabled:opacity-50">
          <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
            <path fill="#4285F4" d="M21.35 11.1H12v2.9h5.35c-.5 2.4-2.55 3.8-5.35 3.8a5.8 5.8 0 1 1 0-11.6c1.45 0 2.75.5 3.8 1.5l2.15-2.15A8.9 8.9 0 0 0 12 3.1a8.9 8.9 0 1 0 0 17.8c4.45 0 8.55-3.2 8.55-8.9 0-.3-.05-.6-.2-.9Z"/>
          </svg>
          Add to Google Wallet
        </button>
      )}
    </div>
  );
};

export default AddToWalletButtons;
